import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

export type Invite = {
  id: string;
  token: string;
  branch_id: string | null;
  job_title_id: string | null;
  role_id: string | null;
  expires_at: string;
  max_uses: number;
  used_count: number;
  created_at: string;
  branches: { name: string } | null;
  job_titles: { name: string } | null;
  roles: { name: string } | null;
  registration_invite_branches: { branches: { name: string } | null }[];
};

export type Application = {
  id: string;
  invite_id: string | null;
  first_name: string;
  last_name: string;
  phone: string;
  phone_verified_at: string | null;
  email: string | null;
  birth_date: string | null;
  address: string | null;
  note: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  /**
   * ההצעות של הקוד שדרכו נרשמו. בלעדיהן טופס האישור נפתח ריק,
   * והמנהל ממלא שוב את מה שכבר קבע כשיצר את הקוד.
   */
  registration_invites: {
    branch_id: string | null;
    job_title_id: string | null;
    role_id: string | null;
    registration_invite_branches: { branch_id: string }[];
  } | null;
};

type ApproveArgs = Database['public']['Functions']['approve_application']['Args'];

const INVITES_KEY = ['registration-invites'] as const;
const APPLICATIONS_KEY = ['employee-applications'] as const;

export function useInvites() {
  return useQuery({
    queryKey: INVITES_KEY,
    queryFn: async (): Promise<Invite[]> => {
      const { data, error } = await supabase
        .from('registration_invites')
        .select(
          `id, token, branch_id, job_title_id, role_id, expires_at, max_uses, used_count, created_at,
           branches ( name ), job_titles ( name ), roles ( name ),
           registration_invite_branches ( branches ( name ) )`,
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Invite[];
    },
  });
}

export function useApplications() {
  return useQuery({
    queryKey: APPLICATIONS_KEY,
    queryFn: async (): Promise<Application[]> => {
      const { data, error } = await supabase
        .from('employee_applications')
        .select(
          `id, invite_id, first_name, last_name, phone, phone_verified_at, email,
           birth_date, address, note, status, created_at,
           registration_invites ( branch_id, job_title_id, role_id,
             registration_invite_branches ( branch_id ) )`,
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Application[];
    },
  });
}

export function useInviteMutations(currentEmployeeId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: INVITES_KEY });

  const create = useMutation({
    mutationFn: async (input: {
      branch_id: string | null;
      extra_branch_ids: string[];
      job_title_id: string | null;
      role_id: string | null;
      hours: number;
      max_uses: number;
    }) => {
      const expires = new Date(Date.now() + input.hours * 3_600_000).toISOString();
      const { data, error } = await supabase
        .from('registration_invites')
        .insert({
          branch_id: input.branch_id,
          job_title_id: input.job_title_id,
          role_id: input.role_id,
          expires_at: expires,
          max_uses: input.max_uses,
          // ה RLS דורש שהיוצר יהיה העובד המחובר
          created_by: currentEmployeeId ?? null,
        })
        .select('id, token')
        .single();
      if (error) throw error;

      if (input.extra_branch_ids.length > 0) {
        const { error: branchesFailed } = await supabase
          .from('registration_invite_branches')
          .insert(
            input.extra_branch_ids.map((branchId) => ({
              invite_id: data.id as string,
              branch_id: branchId,
            })),
          );
        if (branchesFailed) throw branchesFailed;
      }

      return data.token as string;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('registration_invites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, remove };
}

export function useApplicationMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
  };

  /** אישור ודחייה עוברים דרך RPC security definer, לא דרך הטבלה. */
  const approve = useMutation({
    mutationFn: async (input: {
      id: string;
      branch_id: string;
      job_title_id: string | null;
      role_id: string;
    }) => {
      // supabase gen types מסמן כל ארגומנט של פונקציה כ string, גם כשהוא
      // מקבל null. תפקיד הוא שדה רשות (PRD 5.17), ו null הוא ערך תקין כאן.
      const args = {
        p_application: input.id,
        p_branch: input.branch_id,
        p_job_title: input.job_title_id,
        p_role: input.role_id,
      } as unknown as ApproveArgs;

      const { error } = await supabase.rpc('approve_application', args);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('reject_application', { p_application: id });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { approve, reject };
}

/** הכתובת שה QR מצביע אליה. הבסיס מגיע מ app_settings, לא מהקוד. */
export function joinUrl(appUrl: string, token: string): string {
  const base = appUrl.replace(/\/+$/, '');
  return `${base}/join/${token}`;
}

/**
 * כמה בקשות הצטרפות ממתינות. משמש את מסך העובדים כדי שהן לא
 * ייעלמו מהעין אחרי שהטאב הנפרד אוחד לתוכו.
 */
export function usePendingApplicationsCount() {
  return useQuery({
    queryKey: ['pending-applications-count'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('employee_applications')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },
  });
}
